import { ICopyrightWidgetData, ICopyrightWidgetStyle } from "@pageBuilder/typings";
import { WidgetRendererBase } from "./widget-renderer-base";
import { CssProperties } from "src/models/css-property";
import { StringUtil } from "src/utils/string-util";

export class CopyrightWidgetRenderer extends WidgetRendererBase<ICopyrightWidgetData> {

    protected createNewInternal(): HTMLElement {
        const element = this.createWidgetElement("copyrightWidget");
        element.classList.add("copyright");
        return element;
    }

    protected override renderInternal(element: HTMLElement, data: ICopyrightWidgetData): HTMLElement {
        element.setAttribute("data-element-json", JSON.stringify(data));

        const content = element.querySelector(".widget-content") as HTMLElement;
        content.innerHTML = "";

        const p = document.createElement("p");
        p.classList.add("copyright-text");

        const copyrightSign = "&copy;";
        const fromYear = !StringUtil.isNullOrWhiteSpace(data?.fromDate) ? `${data?.fromDate} - ` : "";
        const toDate = !StringUtil.isNullOrWhiteSpace(data?.toDate) ? data.toDate : new Date().getFullYear().toString();

        const fullDateStr = `${fromYear}<span class="to-year">${toDate}</span>`;
        const copyright = "<span class='copyright-label'>Copyright</span>";

        switch (data.layout) {
            case "copyright-sign-date-name":
                p.innerHTML = `${copyright} ${copyrightSign} ${fullDateStr} ${data.businessName}`;
                break;
            default:
                p.innerHTML = `${copyrightSign} ${fullDateStr} ${data.businessName}`;
                break;
        }

        content.append(p);
        this.renderStyles(element, data);
        return element;
    }

    protected renderStyles(element: HTMLElement, data: ICopyrightWidgetData): void {
        this.renderBaseStyles(element, data.style);
        this.renderInnerStyles(element, data.style);
    }

    private renderInnerStyles(element: HTMLElement, style: ICopyrightWidgetStyle | null): void {
        this.cleanupInnerStyles(element);

        const props = new CssProperties();

        if (style?.font?.size) {
            props.tryAdd("--font-size", String(StringUtil.getNumberFromStr(style.font.size)));
        }
        props.tryAdd("--font-family", style?.font.family ?? "");
        props.tryAdd("--font-weight", style?.font.weight ?? "");
        props.tryAdd("--color", style?.font.color ?? "");

        switch (style?.alignment) {
            case "start":
                props.tryAdd("text-align", "left");
                break;
            case "center":
                props.tryAdd("text-align", "center");
                break;
            case "end":
                props.tryAdd("text-align", "right");
                break;
        }

        this.styleService.setStyle(element, `#${element.id} .copyright-text`, props.data);
    }

    private cleanupInnerStyles(element: HTMLElement): void {
        this.styleService.clearStyle(element, `#${element.id} .copyright-text`);
    }

    private clearAlignmentClasses(element: HTMLElement): void {
        element.classList.remove("alignable", "justify-start", "justify-center", "justify-end");
    }

    private renderBaseStyles(element: HTMLElement, style: ICopyrightWidgetStyle | null): void {
        this.clearAlignmentClasses(element);
        element.classList.add("alignable");
        element.classList.add(style ? `justify-${style?.alignment}` : "justify-center");
        this.animationRenderer.setAnimationStyles(element, style?.animation);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override cleanupInternal(element: HTMLElement): void {
        return;
    }

    protected override getContentSelectorsInternal(element: HTMLElement): string[] {
        return [
            `#${element.id}`,
            `#${element.id} .copyright-text`
        ];
    }

}
